import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  subscriptionChangesService,
  type ChangeItemKind,
  type CustomerChangeRequest,
  type CustomerChangeRequestWithItems,
  type PreviewRemovalImpact,
  type SubmitChangeRequest,
} from '@app/shared/services/subscription-changes.service';
import { getMenuByUserService } from '@app/shared/services/menu.service';
import { useMenuStore } from '@app/shared/store/menu.store';

const myChangeKeys = {
  list: ['subscription-changes', 'me'] as const,
};

const sadminChangeKeys = {
  list: (businessId: string) =>
    ['subscription-changes', 'sadmin', businessId] as const,
};

/**
 * Single-purpose mutation for the ModulePaywall "Agregar" confirm
 * flow. Submits a self-service change request + invalidates the
 * owned-items + plan-usage caches so the page unlocks on the next
 * render. The 'subscription' / 'me' prefix is owned by
 * `subscription.hook.ts` but the array is not exported — inlined
 * here to keep that hook's surface tight.
 */
export const useSubmitSubscriptionChange = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, SubmitChangeRequest>({
    mutationFn: (payload) => subscriptionChangesService.submit(payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['subscription', 'me'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['subscription', 'me', 'items'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['sellable-catalog'],
        }),
        queryClient.invalidateQueries({ queryKey: myChangeKeys.list }),
        // Backend already bumps permissions_epoch + auth interceptor
        // refetches /menu/user. We still invalidate locally so the
        // sidebar / page perm gates flip on the next render without
        // waiting for the next authed response.
        queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as readonly unknown[];
            if (!Array.isArray(key) || key.length === 0) return false;
            const first = String(key[0]);
            return (
              first === 'menu' ||
              first.includes('permission') ||
              first.includes('capability') ||
              first.includes('role')
            );
          },
        }),
      ]);
      // Sidebar reads from the persisted Zustand `useMenuStore`, not
      // from TanStack — invalidating the menu query alone does NOT
      // refresh the rendered sidebar. Force a fresh `/menu/user`
      // fetch + write to the store so newly-granted module / addon
      // perms surface immediately after the add succeeds.
      try {
        const fresh = await getMenuByUserService();
        useMenuStore.getState().setMenu(fresh);
      } catch {
        // Auth interceptor's epoch listener will refetch on the next
        // authed response — silent failure here is acceptable.
      }
    },
  });
};

/**
 * Lists every change request owned by the calling business. The
 * /account scheduled-downgrades section filters this to `approved` +
 * non-null `scheduledEffectiveAt` to render the upcoming-access-loss
 * card.
 */
export const useMyChangeRequests = () =>
  useQuery<CustomerChangeRequestWithItems[]>({
    queryKey: myChangeKeys.list,
    queryFn: () => subscriptionChangesService.list(),
    select: (data) => (Array.isArray(data) ? data : []),
  });

/**
 * Cancels a scheduled downgrade. On success invalidates the
 * change-request list AND the owned-items cache so the FE drops the
 * "Baja programada" badge immediately.
 */
export const useCancelScheduledChange = () => {
  const queryClient = useQueryClient();
  return useMutation<CustomerChangeRequest, Error, { id: string }>({
    mutationFn: ({ id }) => subscriptionChangesService.cancel(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: myChangeKeys.list }),
        queryClient.invalidateQueries({
          queryKey: ['subscription', 'me', 'items'],
        }),
        // Cancelling a scheduled downgrade preserves access — same
        // need to flip menu/permission gates back to the pre-schedule
        // shape if the FE had already pre-emptively hidden something.
        queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as readonly unknown[];
            if (!Array.isArray(key) || key.length === 0) return false;
            const first = String(key[0]);
            return (
              first === 'menu' ||
              first.includes('permission') ||
              first.includes('capability') ||
              first.includes('role')
            );
          },
        }),
      ]);
    },
  });
};

/**
 * Sadmin view of any business's change requests. Separate key from
 * `myChangeKeys` because the customer route always answers with the
 * caller's OWN business regardless of the path param.
 */
export const useSadminChangeRequests = (businessId: string) =>
  useQuery<CustomerChangeRequestWithItems[]>({
    queryKey: sadminChangeKeys.list(businessId),
    queryFn: () => subscriptionChangesService.listForBusiness(businessId),
    enabled: Boolean(businessId),
    select: (data) => (Array.isArray(data) ? data : []),
  });

/**
 * Forces an approved change request to materialize now, ahead of its
 * `scheduledEffectiveAt`. Invalidates the sadmin list + the business
 * composition so the modal repaints with the new plan.
 */
export const useApplyChangeRequestNow = (businessId: string) => {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { id: string }>({
    mutationFn: ({ id }) => subscriptionChangesService.applyNow(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: sadminChangeKeys.list(businessId),
        }),
        queryClient.invalidateQueries({
          queryKey: ['sadmin', 'business-composition', businessId],
        }),
        // "Estado actual" card on the subscription-management modal
        // reads useBusinessSubscription (subscription/business/<id>) +
        // useBusinessSubscriptionPayments. Both must refetch so the
        // new plan + payment status paint immediately after apply.
        queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as readonly unknown[];
            if (!Array.isArray(key) || key.length === 0) return false;
            if (key[0] !== 'subscription') return false;
            // Match any subscription/* subtree tied to this business.
            return key.includes(businessId);
          },
        }),
        // "Próxima factura" card reads sadmin billing next-preview +
        // the invoice list — both must refetch.
        queryClient.invalidateQueries({
          queryKey: ['sadmin', 'billing', 'next-preview', businessId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['sadmin', 'billing', 'list', businessId],
        }),
        // Applying revokes SKUs — the operator may be looking at the
        // business through a sadmin surface gated on the same keys.
        queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as readonly unknown[];
            if (!Array.isArray(key) || key.length === 0) return false;
            const first = String(key[0]);
            return (
              first === 'menu' ||
              first.includes('permission') ||
              first.includes('capability') ||
              first.includes('role')
            );
          },
        }),
      ]);
    },
  });
};

/**
 * Customer counterpart of useApplyChangeRequestNow — force-applies a
 * scheduled downgrade the caller OWNS via /subscription-changes/me/:id/apply-now.
 * Same broad invalidation as the sadmin hook so /account repaints
 * with the new plan + menu picks up the new caps immediately.
 * Compliance blocks still surface via the standard errorCode map.
 */
export const useSelfApplyChangeRequestNow = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { id: string }>({
    mutationFn: ({ id }) => subscriptionChangesService.selfApplyNow(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['self', 'change-requests'],
        }),
        queryClient.invalidateQueries({ queryKey: ['self', 'billing'] }),
        queryClient.invalidateQueries({ queryKey: ['self', 'subscription'] }),
        queryClient.invalidateQueries({
          queryKey: ['my-next-invoice-preview'],
        }),
        queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as readonly unknown[];
            if (!Array.isArray(key) || key.length === 0) return false;
            const first = String(key[0]);
            return (
              first === 'menu' ||
              first.includes('permission') ||
              first.includes('capability') ||
              first.includes('role') ||
              first.includes('billing') ||
              first.includes('subscription')
            );
          },
        }),
      ]);
    },
  });
};

/**
 * Dry-run the impact of a removal — powers the "Vas a perder access +
 * tu factura se ajustará a RD$X" preview in the confirm modal. Uses
 * `useMutation` (POST body carries kind+code) instead of `useQuery`
 * so callers can fire it lazily when the user clicks "Quitar" rather
 * than pre-fetching every ownable item's preview.
 */
export const usePreviewRemovalImpact = () =>
  useMutation<
    PreviewRemovalImpact,
    Error,
    { itemKind: ChangeItemKind; targetCode: string }
  >({
    mutationFn: ({ itemKind, targetCode }) =>
      subscriptionChangesService.previewRemoval(itemKind, targetCode),
  });
